import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Coffee, CreditCard, Hotel, Pencil, Plus, ShieldCheck, ShieldX, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/extranet/StatusBadge";
import RoomFormDialog from "@/components/extranet/RoomFormDialog";
import RatePlanDialog from "@/components/extranet/RatePlanDialog";
import { http, idr, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import Img from "@/components/Img";

const planPrice = (base, rp) => Math.max(Math.round(rp.adjust_type === "percent" ? base * (1 + rp.adjust_value / 100) : base + rp.adjust_value), 0);
const ruleText = (rp) => rp.adjust_value === 0 ? "Sama dengan harga kamar" : rp.adjust_type === "percent" ? `${rp.adjust_value > 0 ? "+" : ""}${rp.adjust_value}%` : `${rp.adjust_value > 0 ? "+" : "-"}${idr(Math.abs(rp.adjust_value))}`;
const applies = (rp, rid) => !rp.room_type_ids?.length || rp.room_type_ids.includes(rid);

const Perks = ({ rp }) => (
  <span className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
    <span className="flex items-center gap-1"><Coffee className="h-3.5 w-3.5" />{rp.breakfast_included ? "Sarapan" : "Tanpa sarapan"}</span>
    <span className="flex items-center gap-1">{rp.refundable ? <ShieldCheck className="h-3.5 w-3.5 text-green-600" /> : <ShieldX className="h-3.5 w-3.5 text-red-500" />}{rp.refundable ? "Refundable" : "Non-refundable"}</span>
    <span className="flex items-center gap-1">{rp.payment_type === "pay_at_hotel" ? <Hotel className="h-3.5 w-3.5" /> : <CreditCard className="h-3.5 w-3.5" />}{rp.payment_type === "pay_at_hotel" ? "Bayar di hotel" : "Prepaid"}</span>
  </span>
);

export default function RoomsRatePlans() {
  const { property } = useOutletContext();
  const t = useT();
  const [rooms, setRooms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [roomDlg, setRoomDlg] = useState({ open: false, room: null });
  const [planDlg, setPlanDlg] = useState({ open: false, plan: null });

  const load = () => {
    http.get(`/extranet/properties/${property.id}/rooms`).then((r) => setRooms(r.data));
    http.get(`/extranet/properties/${property.id}/rate-plans`).then((r) => setPlans(r.data));
  };
  useEffect(() => { load(); }, [property.id]); // eslint-disable-line

  const del = async (url, label) => {
    if (!window.confirm(`Hapus ${label}?`)) return;
    try { await http.delete(url); toast.success(`${label} dihapus`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fade-up flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t("Tipe Kamar & Rate Plan")}</h1>
          <p className="text-sm text-slate-500">{t("Harga rate plan diturunkan dari harga kamar di kalender Tarif & Ketersediaan.")}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="add-room-button" onClick={() => setRoomDlg({ open: true, room: null })} className="flex items-center gap-1.5 rounded-md border border-[#006CE4] bg-white px-3 py-2 text-sm font-semibold text-[#006CE4] hover:bg-blue-50"><Plus className="h-4 w-4" /> {t("Tipe kamar")}</button>
          <button data-testid="add-rate-plan-button" onClick={() => setPlanDlg({ open: true, plan: null })} className="flex items-center gap-1.5 rounded-md bg-[#006CE4] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0052B4]"><Plus className="h-4 w-4" /> {t("Rate plan")}</button>
        </div>
      </div>

      <Panel title="Daftar tipe kamar & rate plan" testid="room-rateplan-list">
        <div className="divide-y divide-slate-200">
          {rooms.map((r) => (
            <div key={r.id} data-testid={`room-item-${r.id}`}>
              <div className="flex flex-wrap items-center gap-3 bg-slate-50 px-5 py-3">
                <Img src={r.images[0]} size="sm" className="h-10 w-14 rounded object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="font-ex-heading font-bold">{r.name}</p>
                  <p className="text-xs text-slate-500">{[`${r.total_rooms} kamar`, `maks ${r.max_guests} tamu`, r.bed, `harga dasar ${idr(r.base_price)}`].filter(Boolean).join(" · ")}</p>
                </div>
                <button data-testid={`edit-room-${r.id}`} onClick={() => setRoomDlg({ open: true, room: r })} className="rounded p-2 text-slate-500 hover:bg-slate-200"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-room-${r.id}`} onClick={() => del(`/extranet/rooms/${r.id}`, `tipe kamar ${r.name}`)} className="rounded p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {plans.filter((rp) => applies(rp, r.id)).map((rp) => (
                    <tr key={rp.id} data-testid={`room-plan-${r.id}-${rp.id}`} className={rp.active ? "" : "opacity-50"}>
                      <td className="py-2.5 pl-12 pr-3 font-medium">{rp.name}{!rp.active && <span className="ml-2 rounded bg-slate-200 px-1.5 text-[10px] font-semibold uppercase">Nonaktif</span>}</td>
                      <td className="px-3"><Perks rp={rp} /></td>
                      <td className="px-3 text-xs text-slate-500">{ruleText(rp)}</td>
                      <td className="pr-5 text-right font-semibold">{idr(planPrice(r.base_price, rp))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Kelola rate plan" testid="rate-plans-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-xs uppercase text-slate-500">
              <tr><th className="px-5 py-2.5">Rate plan</th><th>Berlaku untuk</th><th>Aturan harga</th><th>Kondisi</th><th>Min. malam</th><th>Status</th><th className="pr-5" /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {plans.map((rp) => (
                <tr key={rp.id} data-testid={`rate-plan-row-${rp.id}`}>
                  <td className="px-5 py-3 font-semibold">{rp.name}</td>
                  <td className="text-xs text-slate-600">{rp.room_type_ids?.length ? rooms.filter((r) => rp.room_type_ids.includes(r.id)).map((r) => r.name).join(", ") : "Semua tipe kamar"}</td>
                  <td className="text-xs">{ruleText(rp)}</td>
                  <td><Perks rp={rp} /></td>
                  <td className="text-center">{rp.min_nights}</td>
                  <td><span className={`rounded px-2 py-0.5 text-xs font-semibold ${rp.active ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"}`}>{rp.active ? "Aktif" : "Nonaktif"}</span></td>
                  <td className="whitespace-nowrap pr-5 text-right">
                    <button data-testid={`edit-rate-plan-${rp.id}`} onClick={() => setPlanDlg({ open: true, plan: rp })} className="rounded p-2 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-rate-plan-${rp.id}`} onClick={() => del(`/extranet/rate-plans/${rp.id}`, `rate plan ${rp.name}`)} className="rounded p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <RoomFormDialog open={roomDlg.open} onOpenChange={(o) => setRoomDlg({ ...roomDlg, open: o })} room={roomDlg.room} propertyId={property.id} onSaved={load} />
      <RatePlanDialog open={planDlg.open} onOpenChange={(o) => setPlanDlg({ ...planDlg, open: o })} plan={planDlg.plan} rooms={rooms} propertyId={property.id} onSaved={load} />
    </div>
  );
}
